import { useState, useEffect, useRef } from "react";
import { getUserId } from "../../utils/auth";
import { Header } from "./components/Header/Header";
import { StatsSection } from "./components/StatsSection/StatsSection";
import { useGetApiData } from "./hooks/useGetApiData";
import { ProfileInfo } from "./components/ProfileInfo/ProfileInfo";
import { commonStyles, mentorProfileStyles } from "./styles";
import { Ratings } from "./components/Ratings";
import useFetch from "../../hooks/useFetch";
import { toast } from "react-toastify";
import { Courses } from "./components/Courses/Courses";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { AboutMe } from "./components/AboutMe/AboutMe";
import { DESKTOP_BREAKPOINT } from "./consts";
import { EditProfileModal } from "./components/EditProfileModal/EditProfileModal";
import { MentorProfileShimmer } from "./components/MentorProfileShimmer";
import { AddPanAndBankDetails } from "./components/AddPanAndBankDetails/AddPanAndBankDetails";


export const MentorProfile = () => {
  const userId = getUserId();
  const classes = mentorProfileStyles();
  const commonClasses = commonStyles();

  const [profileData, setProfiledata] = useState();
  const [mentorCourses, setMentorCourses] = useState();

  const [langState, setLangState] = useState({ selected: [], added: [], deleted: [] });
  const [allLanguages, setAllLanguages] = useState([]);
  const originalLanguagesRef = useRef([]);
  const [ratingData, setRatingData] = useState();
  const [feedbackRatings, setFeedbackRatings] = useState();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const theme = useTheme();
  const belowDesktop = useMediaQuery(
    theme.breakpoints.down(DESKTOP_BREAKPOINT),
  );

  const params = { mentorId: userId, isProfilePage: 1 };

  const { loading: profileDataLoading } = useGetApiData(
    "/getMentorProfile",
    "profileData",
    (data) => {
      setProfiledata(data?.mentorProfileDetails);

      const toStr = (l) => (typeof l === "string" ? l : l?.language);
      const dbLangs = (data?.mentorProfileDetails?.mentorLanguages || []).map(toStr).filter(Boolean);
      const allLangs = (data?.mentorProfileDetails?.allLanguages || []).map(toStr).filter(Boolean);
      const fallbackAll = allLangs.length
        ? allLangs
        : [
          ...dbLangs,
          ...(data?.mentorProfileDetails?.availableMentorLanguages || [])
            .map(toStr)
            .filter(Boolean),
        ];
      originalLanguagesRef.current = dbLangs;
      setLangState({ selected: dbLangs, added: [], deleted: [] });
      setAllLanguages([...new Set(fallbackAll)]);
    },
    params,
  );

  const { loading: mentorCoursesLoading } = useGetApiData(
    "/getMentorCourses",
    "mentorCourses",
    (data) =>
      setMentorCourses(
        data?.mentorCourses.filter((course) => course.restricted !== 1),
      ),
    params,
    "post",
  );

  const { mutate: getFeedbackRating, loading: feedbackRatingLoading } =
    useFetch(`/getRatingStudentWise`, {
      manual: true,
      onSuccess: (data) => setFeedbackRatings(data?.data),
    });

  const { loading: ratingsLoading } = useGetApiData(
    "/getOverallRatings",
    "ratings",
    (data) => {
      setRatingData(data);
      getFeedbackRating({
        params: {
          section: "regular",
          page: "1",
          limit: data?.summary?.total,
        },
      });
    },
  );

  const toggleLanguage = (lang) => {
    const wasInDB = originalLanguagesRef.current.includes(lang);
    setLangState((prev) => {
      const isSelected = prev.selected.includes(lang);
      if (isSelected) {
        return {
          selected: prev.selected.filter((l) => l !== lang),
          added: prev.added.filter((l) => l !== lang),
          deleted: wasInDB ? [...prev.deleted, lang] : prev.deleted,
        };
      } else {
        return {
          selected: [...prev.selected, lang],
          added: !wasInDB ? [...prev.added, lang] : prev.added,
          deleted: prev.deleted.filter((l) => l !== lang),
        };
      }
    });
  };

  const { mutate: saveMockDemoVideo } = useFetch("/mentor/mock-demo-video", {
    method: "put",
    manual: true,
    onError: () => {
      toast.error("Error saving mock demo video!", {
        position: toast.POSITION.TOP_CENTER,
      });
    },
  });

  const { mutate: deleteMockDemoVideo } = useFetch("/mentor/mock-demo-video", {
    method: "delete",
    manual: true,
    onError: () => {
      toast.error("Error deleting mock demo video!", {
        position: toast.POSITION.TOP_CENTER,
      });
    },
  });

  const { mutate: updateProfileInfo, loading: updateInfoLoading } = useFetch(
    "/ediMentorProfile",
    {
      method: "put",
      manual: true,
      onSuccess: () => {
        toast.success("Profile info update.", {
          position: toast.POSITION.TOP_CENTER,
        });
      },
      onError: () => {
        toast.error("Error while saving profile info!", {
          position: toast.POSITION.TOP_CENTER,
        });
      },
    },
  );

  const handleOnProfileInfoSave = async () => {
    await updateProfileInfo({
      data: {
        FNAME: profileData?.FNAME,
        LNAME: profileData?.LNAME,
        contact_no: profileData?.contact_no,
        about: profileData?.about,
        degree: profileData?.degree,
        college: profileData?.college,
        totalYears: profileData?.totalYears,
        totalClasses: profileData?.totalClasses,
        totalStudents: profileData?.totalStudents,
        mockDemoVideoUrl: profileData?.mockDemoVideoUrl ?? "",
        addLanguages: langState.added,
        deleteLanguages: langState.deleted,
      },
    });
    originalLanguagesRef.current = langState.selected;
    setLangState((prev) => ({ ...prev, added: [], deleted: [] }));
  };

  if (profileDataLoading) return <MentorProfileShimmer />;

  if (belowDesktop) {
    return (
      <div className={classes.mobileWrapper}>
        <EditProfileModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          profileData={profileData}
          setProfileData={setProfiledata}
          langState={langState}
          allLanguages={allLanguages}
          toggleLanguage={toggleLanguage}
          handleOnProfileInfoSave={handleOnProfileInfoSave}
          updateInfoLoading={updateInfoLoading}
          saveMockDemoVideo={saveMockDemoVideo}
        />
        <Header
          profileData={profileData}
          languages={langState.selected}
          subjects={profileData?.subjects || []}
          onViewProfile={() =>
            window.open(
              `${process.env.REACT_APP_VIEW_MENTOR_PROFILE}${userId}`,
              "_blank",
            )
          }
          onOpenEdit={() => setIsEditModalOpen(true)}
        />
        <StatsSection profileData={profileData} />
        <main className={classes.wrapper}>
          <ProfileInfo
            profileData={profileData}
            setProfileData={setProfiledata}
            languages={langState.selected}
            setLanguages={toggleLanguage}
            updateInfoLoading={updateInfoLoading}
            handleOnProfileInfoSave={handleOnProfileInfoSave}
            saveMockDemoVideo={saveMockDemoVideo}
            deleteMockDemoVideo={deleteMockDemoVideo}
            onOpenEdit={() => setIsEditModalOpen(true)}
          />

          <AddPanAndBankDetails />

          <AboutMe
            profileData={profileData}
            setProfileData={setProfiledata}
            languages={langState.selected}
            setLanguages={toggleLanguage}
            updateInfoLoading={updateInfoLoading}
            handleOnProfileInfoSave={handleOnProfileInfoSave}
            profileDataLoading={profileDataLoading}
            isProfilePage={false}
            onOpenEdit={() => setIsEditModalOpen(true)}
          />

          <div className={commonClasses.divider} />

          <Courses
            mentorCourses={mentorCourses}
            mentorCoursesLoading={mentorCoursesLoading}
          />

          <div className={commonClasses.divider} />
          <Ratings
            ratingData={ratingData}
            feedbackRatings={feedbackRatings}
            getFeedbackRating={getFeedbackRating}
            feedbackRatingLoading={feedbackRatingLoading}
            ratingsLoading={ratingsLoading}
          />
        </main>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        profileData={profileData}
        setProfileData={setProfiledata}
        langState={langState}
        allLanguages={allLanguages}
        toggleLanguage={toggleLanguage}
        handleOnProfileInfoSave={handleOnProfileInfoSave}
        updateInfoLoading={updateInfoLoading}
        saveMockDemoVideo={saveMockDemoVideo}
      />
      <Header
        profileData={profileData}
        languages={langState.selected}
        subjects={profileData?.subjects || []}
        onViewProfile={() =>
          window.open(
            `${process.env.REACT_APP_VIEW_MENTOR_PROFILE}${userId}`,
            "_blank",
          )
        }
        onOpenEdit={() => setIsEditModalOpen(true)}
      />
      <StatsSection profileData={profileData} />
      <main className={classes.wrapper}>
        <AddPanAndBankDetails />

        <ProfileInfo
          profileData={profileData}
          setProfileData={setProfiledata}
          languages={langState.selected}
          setLanguages={toggleLanguage}
          updateInfoLoading={updateInfoLoading}
          handleOnProfileInfoSave={handleOnProfileInfoSave}
          saveMockDemoVideo={saveMockDemoVideo}
          deleteMockDemoVideo={deleteMockDemoVideo}
          onOpenEdit={() => setIsEditModalOpen(true)}
        />

        <div className={commonClasses.divider} />

        {<Courses mentorCourses={mentorCourses} />}

        <div className={commonClasses.divider} />

        <Ratings
          ratingData={ratingData}
          feedbackRatings={feedbackRatings}
          getFeedbackRating={getFeedbackRating}
          feedbackRatingLoading={feedbackRatingLoading}
        />
      </main>
    </div>
  );
};

export default MentorProfile;
